// useAllItemsBootstrap.ts
import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query'
import { baseUrl } from 'api/config'
import axios from 'axios'

export type AllItems = { organizations: any[]; deals: any[]; products: any[]; parentClients: any[] }

export const KEY = ['organizations_table', 'allItems'] as const
export const ETAG_KEY = ['organizations_table', 'allItems', '__etag__'] as const
export const BOOTSTRAP_REFRESH_STATE_KEY = ['organizations_table', 'allItems', '__refresh_stamp__'] as const
const ETAG_STORE_KEY = '__BOOTSTRAP_ETAG__'
export const resetBootstrapETag = (qc?: QueryClient) => {
  if (qc) qc.setQueryData(ETAG_KEY, null)
  ;(globalThis as any).__BOOTSTRAP_ETAG__ = null
  try {
    sessionStorage.removeItem(ETAG_STORE_KEY)
  } catch {
    /* empty */
  }
}

function setEtag(qc: QueryClient, tag: string | null) {
  qc.setQueryData(ETAG_KEY, tag)
  ;(globalThis as any).__BOOTSTRAP_ETAG__ = tag
  try {
    if (tag) sessionStorage.setItem(ETAG_STORE_KEY, tag)
    else sessionStorage.removeItem(ETAG_STORE_KEY)
  } catch {
    /* empty */
  }
}
function debugForceFreshTrace() {
  try {
    throw new Error('forceFresh caller')
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[bootstrap] forceFresh trace\n', (e as Error).stack?.split('\n').slice(2, 10).join('\n'))
  }
}
// 👉 Reusable fetcher
export async function bootstrapQueryFn(qc: QueryClient, opts?: { forceFresh?: boolean }): Promise<AllItems> {
  if (opts?.forceFresh) debugForceFreshTrace()
  const stored =
    qc.getQueryData<string | null>(ETAG_KEY) ??
    (globalThis as any).__BOOTSTRAP_ETAG__ ??
    (() => {
      try {
        return sessionStorage.getItem(ETAG_STORE_KEY)
      } catch {
        return null
      }
    })()

  const currentTag = stored ?? null
  console.log('[bootstrap] request', { forceFresh: !!opts?.forceFresh, ifNoneMatch: !!currentTag })

  const useForce = !!opts?.forceFresh

  // If forcing: don't send If-None-Match; use ?fresh=1 to bypass 304 path on server
  const resp = await axios.get<AllItems>(`${baseUrl}/aws/organizations_table/bootstrap`, {
    params: useForce ? { fresh: '1' } : undefined,
    headers: !useForce && currentTag ? { 'If-None-Match': currentTag } : undefined,
    validateStatus: s => (s >= 200 && s < 300) || s === 304,
  })
  console.log('[bootstrap] response', { status: resp.status, etag: resp.headers?.etag })
  if (resp.status === 304) {
    // Serve cached copy if we have one
    const cached = qc.getQueryData<AllItems>(KEY)
    if (cached) return cached

    // Safety fallback: do a pure force-fresh fetch (no If-None-Match, with ?fresh=1)
    const full = await axios.get<AllItems>(`${baseUrl}/aws/organizations_table/bootstrap`, {
      params: { fresh: '1' },
    })
    setEtag(qc, (full.headers?.etag as string) ?? null)
    return full.data ?? { organizations: [], deals: [], products: [], parentClients: [] }
  }

  setEtag(qc, (resp.headers?.etag as string) ?? null)
  return resp.data ?? { organizations: [], deals: [], products: [], parentClients: [] }
}

export const useAllItemsBootstrap = () => {
  const qc = useQueryClient()

  // 👉 if someone already observes this query, do not fetch again.
  const entry = qc.getQueryCache().find({ queryKey: KEY })
  const _alreadyObserved = !!entry && entry.getObserversCount() > 0

  return useQuery({
    queryKey: KEY,
    // only the *first* observer provides the queryFn (causing a fetch)
    queryFn: () => bootstrapQueryFn(qc),

    // if we’re not the first, we still want to subscribe to updates
    enabled: true,

    // never refetch on mount; health hook / explicit paths will drive refreshes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: 60 * 60 * 1000,
    refetchIntervalInBackground: true,
    staleTime: 5 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    retry: 3,
    retryDelay: a => Math.min(1000 * 2 ** a, 15_000) + Math.floor(Math.random() * 1000),

    // ensure we render immediately with whatever is in cache
    initialData: () => qc.getQueryData<AllItems>(KEY),
  }) // TS: queryFn can be undefined when alreadyObserved
}
